/**
 * Aires empilées — utilisé pour « nouveaux vs revenants ».
 *
 * L'empilement est le bon dessin ici parce que les deux séries se somment en un
 * tout qui a un sens (les actifs du jour). Sur deux quantités indépendantes, il
 * mentirait : on prendrait la hauteur du haut pour sa valeur.
 */

import {
  ChartFrame,
  DEFAULT_PAD,
  Legend,
  useMeasuredWidth,
  alignSeries,
  describeSeries,
  formatAxisValue,
  niceTicks,
  stackedMax,
  useTimeHover,
} from './chartUtils';
import type { Series } from './chartUtils';
import { formatDateTime, formatInt } from '../lib/format';

export interface AreaStackProps {
  title: string;
  series: Series[];
  height?: number;
  formatX?: (t: string) => string;
  emptyLabel?: string;
}

export function AreaStack(props: AreaStackProps): React.ReactElement {
  const { title, series, height = 230, formatX = formatDateTime, emptyLabel } = props;

  const [frameRef, vbWidth] = useMeasuredWidth<HTMLElement>();
  const { ts, values } = alignSeries(series);
  const pad = DEFAULT_PAD;
  const plotW = vbWidth - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const { hover, onPointerMove, onPointerLeave } = useTimeHover(ts.length, vbWidth, pad);

  const max = stackedMax(values);
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] ?? 1;
  const xOf = (i: number): number =>
    ts.length > 1 ? pad.left + (i / (ts.length - 1)) * plotW : pad.left + plotW / 2;
  const yOf = (v: number): number => pad.top + plotH - (v / top) * plotH;

  // Cumuls successifs : la bande d'une série va de son cumul précédent au sien.
  const cumulative: number[][] = [];
  let running = new Array<number>(ts.length).fill(0);
  for (const row of values) {
    running = running.map((acc, i) => acc + (row[i] ?? 0));
    cumulative.push([...running]);
  }

  const labelEvery = Math.max(1, Math.ceil(ts.length / Math.max(2, Math.floor(plotW / 90))));

  const areaPath = (upper: number[], lower: number[]): string => {
    const up = upper.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(2)},${yOf(v).toFixed(2)}`).join(' ');
    const down = [...lower]
      .map((v, i) => ({ v, i }))
      .reverse()
      .map(({ v, i }) => `L${xOf(i).toFixed(2)},${yOf(v).toFixed(2)}`)
      .join(' ');
    return `${up} ${down} Z`;
  };

  const hoverT = hover ? ts[hover.index] : undefined;

  return (
    <ChartFrame
      ariaLabel={describeSeries(title, series)}
      empty={ts.length === 0 || max === 0}
      emptyLabel={emptyLabel}
      legend={<Legend items={series} />}
      frameRef={frameRef}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      tooltip={
        hover && hoverT !== undefined ? (
          <div className="chart-tip" style={{ left: `${hover.leftPct}%`, top: 0 }}>
            <span className="t-when">{formatX(hoverT)}</span>
            {series.map((s, si) => (
              <span className="t-row" key={s.key}>
                <i className="t-swatch" style={{ background: s.color }} />
                {s.label} {formatInt(values[si]?.[hover.index] ?? 0)}
              </span>
            ))}
          </div>
        ) : undefined
      }
    >
      <svg viewBox={`0 0 ${vbWidth} ${height}`} role="img" aria-label={describeSeries(title, series)}>
        {ticks.map((tick) => (
          <g key={tick}>
            <line className="grid-line" x1={pad.left} x2={vbWidth - pad.right} y1={yOf(tick)} y2={yOf(tick)} />
            <text className="axis-label" x={pad.left - 8} y={yOf(tick) + 3.5} textAnchor="end">
              {formatAxisValue(tick)}
            </text>
          </g>
        ))}

        {series.map((s, si) => {
          const upper = cumulative[si];
          const lower = si === 0 ? new Array<number>(ts.length).fill(0) : cumulative[si - 1];
          if (!upper || !lower) return null;
          return (
            <g key={s.key}>
              <path d={areaPath(upper, lower)} fill={s.color} opacity={0.72} />
              <path
                d={upper.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(2)},${yOf(v).toFixed(2)}`).join(' ')}
                fill="none"
                stroke={s.color}
                strokeWidth={1.6}
              />
            </g>
          );
        })}

        {ts.map((t, i) =>
          i % labelEvery === 0 ? (
            <text
              key={t}
              className="axis-label"
              x={xOf(i)}
              y={height - 8}
              textAnchor={i === 0 ? 'start' : i === ts.length - 1 ? 'end' : 'middle'}
            >
              {formatX(t)}
            </text>
          ) : null,
        )}

        {hover ? (
          <line
            x1={xOf(hover.index)}
            x2={xOf(hover.index)}
            y1={pad.top}
            y2={pad.top + plotH}
            stroke="var(--c-ink)"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        ) : null}
      </svg>
    </ChartFrame>
  );
}
